import { Injectable } from '@angular/core';
import { Http } from '@core/http/http';
import { INotPlantedReport, INotPlantedReportCriteria, IOldNewId, IPlantingReport, IPlantingReportCriteria, IReport, IReportHistory, ReportStatus, ReportType, RegulatedRegCompReportType } from '@reporting/interfaces';
import { IFinalFieldTestReport, IFinalFieldTestReportCriteria, IFinalFieldTestReportLineItem } from '@reporting/interfaces/final-field-test-report';
import { IVolunteerReport, IVolunteerReportCriteria, IVolunteerReportLineItem } from '@reporting/interfaces/volunteer-report';
import { environment } from '@app/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IRegulatedRegCompReportCriteria, IRegulatedRegCompReport } from '@reporting/interfaces/regulated-regcomp-report';
import { IStewardedRegCompReportCriteria, IStewardedRegCompReport } from '@reporting/interfaces/stewarded-regcomp-report';
import { MainMenuService } from '@shared/services/main-menu.service';

@Injectable({
    providedIn: 'root'
})
export class ReportService {
    constructor(private http: Http, private mainMenuService: MainMenuService) { }

    public RunPlantingReport(criteria: IPlantingReportCriteria): Observable<number> {
        return this.http.post(`${environment.apiRoot}api/v1/Reports/Submit/PlantingReport`, criteria);
    }

    public GetHistory(lastReportId = 0): Observable<IReportHistory[]> {
        return this.http.get(`${environment.apiRoot}api/v1/Reports/History/Get?lastReportId=${lastReportId}`);
    }

    public GetPlantingReport(reportId: number): Observable<IPlantingReport> {
        return this.http.get(`${environment.apiRoot}api/v1/Reports/Get?reportType=${ReportType.Planting}&reportId=${reportId}`)
            .pipe(map((report: IPlantingReport) => {
                this.mapReport(report);
                return report;
            }));
    }

    public RunNotPlantedReport(criteria: INotPlantedReportCriteria): Observable<number> {
        return this.http.post(`${environment.apiRoot}api/v1/Reports/Submit/NotPlantedReport`, criteria);
    }

    public GetNotPlantedReport(reportId: number): Observable<INotPlantedReport> {
        return this.http.get(`${environment.apiRoot}api/v1/Reports/Get?reportType=${ReportType.NotPlanted}&reportId=${reportId}`)
            .pipe(map((report: INotPlantedReport) => {
                this.mapReport(report);
                return report;
            }));
    }

    public RunVolunteerReport(criteria: IVolunteerReportCriteria): Observable<number> {
        return this.http.post(`${environment.apiRoot}api/v1/Reports/Submit/VolunteerReport`, criteria);
    }

    public CheckForVolunteerDraftData(criteria: IVolunteerReportCriteria): Observable<IVolunteerReportCriteria> {
        return this.http.post(`${environment.apiRoot}api/v1/Reports/CheckForDraftData/VolunteerReport`, criteria);
    }

    public GetVolunteerReport(reportId: number): Observable<IVolunteerReport> {
        return this.http.get(`${environment.apiRoot}api/v1/Reports/Get?reportType=${ReportType.Volunteer}&reportId=${reportId}`)
            .pipe(map((report: IVolunteerReport) => {
                this.mapReport(report);
                return report;
            }));
    }

    public RunFinalFieldTestReport(criteria: IFinalFieldTestReportCriteria): Observable<number> {
        return this.http.post(`${environment.apiRoot}api/v1/Reports/Submit/FinalFieldTestReport`, criteria);
    }

    public CheckForFinalFieldTestDraftData(criteria: IFinalFieldTestReportCriteria): Observable<IFinalFieldTestReportCriteria> {
        return this.http.post(`${environment.apiRoot}api/v1/Reports/CheckForDraftData/FinalFieldTestReport`, criteria);
    }

    public GetFinalFieldTestReport(reportId: number): Observable<IFinalFieldTestReport> {
        return this.http.get(`${environment.apiRoot}api/v1/Reports/Get?reportType=${ReportType.FinalFieldTest}&reportId=${reportId}`)
            .pipe(map((report: IFinalFieldTestReport) => {
                this.mapReport(report);
                return report;
            }));
    }

    public RunRegulatedRegCompReport(criteria: IRegulatedRegCompReportCriteria): Observable<number> {
        return this.http.post(`${environment.apiRoot}api/v1/Reports/Submit/RegulatedRegCompReport`, criteria);
    }

    public GetRegulatedRegCompReport(reportId: number, jobType?: RegulatedRegCompReportType): Observable<IRegulatedRegCompReport> {
        return this.http.get(`${environment.apiRoot}api/v1/Reports/Get?reportType=${ReportType.RegulatedRegComp}&reportId=${reportId}&jobType=${jobType}`)
            .pipe(map((report: IRegulatedRegCompReport) => {
                this.mapReport(report);
                return report;
            }));
    }

    public RunStewardedRegCompReport(criteria: IStewardedRegCompReportCriteria): Observable<number> {
        return this.http.post(`${environment.apiRoot}api/v1/Reports/Submit/StewardedRegCompReport`, criteria);
    }

    public GetStewardedRegCompReport(reportId: number): Observable<IStewardedRegCompReport> {
        return this.http.get(`${environment.apiRoot}api/v1/Reports/Get?reportType=${ReportType.StewardedRegComp}&reportId=${reportId}`)
            .pipe(map((report: IStewardedRegCompReport) => {
                this.mapReport(report);
                return report;
            }));
    }

    public MarkAsViewed(reportIds: number | number[]) {
        let list: number[] = [];

        if (reportIds instanceof Array) {
            list = reportIds;
        }
        else {
            list.push(reportIds);
        }

        list.forEach((reportId: number) => {
            this.mainMenuService.unviewedReports[reportId] = false;
        });

        return this.http.post(`${environment.apiRoot}api/v1/Reports/UpdateToViewedReport`, list);
    }

    public DeleteReports(reportIds: number | number[]) {
        let list: number[] = [];

        if (reportIds instanceof Array) {
            list = reportIds;
        }
        else {
            list.push(reportIds);
        }

        return this.http.post(`${environment.apiRoot}api/v1/Reports/Delete`, list);
    }

    public SaveVolunteerDraftData(reportId: number, lineItems: IVolunteerReportLineItem[]): Observable<IOldNewId[]> {
        return this.http.post(`${environment.apiRoot}api/v1/Reports/SaveDraftData/VolunteerReport?reportId=${reportId}`, lineItems);
    }

    public SaveFinalFieldTestDraftData(reportId: number, lineItems: IFinalFieldTestReportLineItem[]): Observable<IOldNewId[]> {
        return this.http.post(`${environment.apiRoot}api/v1/Reports/SaveDraftData/FinalFieldTestReport?reportId=${reportId}`, lineItems);
    }

    private mapReport(report: IReport): void {
        report.AddDate = new Date(report.AddDate);

        if (String(report.FinishDate) === '0001-01-01T00:00:00+00:00') {
            delete report.FinishDate;
        }

        if (report.FinishDate !== undefined) {
            report.FinishDate = new Date(report.FinishDate);
        }

        if (report.StartDate !== undefined) {
            report.StartDate = new Date(report.StartDate);
        }

        if (report.Status !== ReportStatus.Finished) {
            delete report.RunDurationMs;
            delete report.RowCount;
        }
    }
}
